import React, { useState } from 'react';
import { RiCalendarLine, RiMapPinLine, RiUserLine, RiCloseCircleLine, RiHashtag } from '@remixicon/react';
import QuoteSearchByDateModal from './QuoteSearchByDateModal';
import QuoteSearchByAirportModal from './QuoteSearchByAirportModal';
import QuoteSearchByClientModal from './QuoteSearchByClientModal';
import QuoteSearchByQuoteNumberModal from './QuoteSearchByQuoteNumberModal';

const SEARCH_TYPES = {
  NONE: 'none',
  DATE: 'date',
  AIRPORT: 'airport',
  CLIENT: 'client',
  QUOTE_NUMBER: 'quote_number',
};

const ICONS = {
  [SEARCH_TYPES.DATE]: RiCalendarLine,
  [SEARCH_TYPES.AIRPORT]: RiMapPinLine,
  [SEARCH_TYPES.CLIENT]: RiUserLine,
  [SEARCH_TYPES.QUOTE_NUMBER]: RiHashtag,
};

const BUTTONS = [
  { type: SEARCH_TYPES.DATE,         label: 'Date' },
  { type: SEARCH_TYPES.AIRPORT,      label: 'Airport' },
  { type: SEARCH_TYPES.CLIENT,       label: 'Client' },
  { type: SEARCH_TYPES.QUOTE_NUMBER, label: 'Trip No.' },
];

/**
 * QuoteSearchBar
 * Mirrors the mobile QuotesScreen "Search By" UI: four pill buttons
 * (Date / Airport / Client / Quote #) that each open a picker modal.
 * When a search is active, a dismissible chip is shown with the picked
 * label.
 *
 * Props:
 *   searchType  — current SEARCH_TYPES value (or 'none')
 *   searchLabel — display string for the active chip
 *   brokerId    — optional broker_id to scope the client picker
 *   onSelect(type, value, label) — fired after a picker confirms
 *   onClear()                    — fired when the chip's Clear is clicked
 */
const QuoteSearchBar = ({
  searchType = SEARCH_TYPES.NONE,
  searchValue = null,
  searchLabel = '',
  brokerId = null,
  onSelect,
  onClear,
}) => {
  const [openModal, setOpenModal] = useState(SEARCH_TYPES.NONE);

  const handleOpen = (type) => setOpenModal(type);
  const handleClose = () => setOpenModal(SEARCH_TYPES.NONE);

  const handlePicked = (type) => (value, label) => {
    setOpenModal(SEARCH_TYPES.NONE);
    onSelect?.(type, value, label);
  };

  const ActiveIcon = ICONS[searchType];

  return (
    <div>
      {/* Single inline row: "Search by:" label + four pill buttons + (when
          active) the value chip and Clear button. Wraps gracefully on narrow
          viewports. */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="text-sm font-medium text-gray-600 mr-1">Search by:</span>
        {BUTTONS.map(({ type, label }) => {
          const Icon = ICONS[type];
          const isActive = searchType === type;
          return (
            <button
              key={type}
              type="button"
              onClick={() => handleOpen(type)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-700 border-primary-200'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          );
        })}

        {searchType !== SEARCH_TYPES.NONE && (
          <>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm rounded-md bg-primary-50 text-primary-700 max-w-[16rem] truncate">
              {ActiveIcon && <ActiveIcon className="w-3.5 h-3.5 shrink-0" />}
              <span className="truncate">{searchLabel || 'Filtered'}</span>
            </span>
            <button
              type="button"
              onClick={onClear}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <RiCloseCircleLine className="w-3.5 h-3.5" />
              Clear
            </button>
          </>
        )}
      </div>

      {/* Modals */}
      <QuoteSearchByDateModal
        isOpen={openModal === SEARCH_TYPES.DATE}
        onClose={handleClose}
        onSelect={handlePicked(SEARCH_TYPES.DATE)}
        initialDate={searchType === SEARCH_TYPES.DATE ? searchValue : ''}
      />
      <QuoteSearchByAirportModal
        isOpen={openModal === SEARCH_TYPES.AIRPORT}
        onClose={handleClose}
        onSelect={handlePicked(SEARCH_TYPES.AIRPORT)}
      />
      <QuoteSearchByClientModal
        isOpen={openModal === SEARCH_TYPES.CLIENT}
        onClose={handleClose}
        onSelect={handlePicked(SEARCH_TYPES.CLIENT)}
        brokerId={brokerId}
      />
      <QuoteSearchByQuoteNumberModal
        isOpen={openModal === SEARCH_TYPES.QUOTE_NUMBER}
        onClose={handleClose}
        onSelect={handlePicked(SEARCH_TYPES.QUOTE_NUMBER)}
        initialQuery={searchType === SEARCH_TYPES.QUOTE_NUMBER ? String(searchValue || '') : ''}
      />
    </div>
  );
};

QuoteSearchBar.SEARCH_TYPES = SEARCH_TYPES;
QuoteSearchBar.QUOTE_NUMBER_MIN_LENGTH = QuoteSearchByQuoteNumberModal.MIN_LENGTH;

export default QuoteSearchBar;
